import { useMemo, useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { darkTheme, lightTheme } from './theme';

// Route-specific theme overrides that take precedence over authentication state
const ROUTE_THEME_OVERRIDES: Record<string, 'light' | 'dark'> = {
  '/login': 'light',
};

/**
 * Custom hook that returns the appropriate theme based on route overrides and authentication status.
 *
 * Priority order:
 * 1. Route-specific overrides (highest priority)
 * 2. Authentication state (fallback)
 *    - Dark theme for public routes (when not authenticated)
 *    - Light theme for private routes (when authenticated)
 */
export const useAppTheme = () => {
  const { isAuthenticated } = useAuthStore();
  const currentPath = useCurrentPath();

  const theme = useMemo(() => {
    // Check for route-specific theme override first
    const routeOverride = ROUTE_THEME_OVERRIDES[currentPath];

    if (routeOverride) {
      return routeOverride === 'light' ? lightTheme : darkTheme;
    }

    // Fall back to authentication-based theme selection
    return isAuthenticated ? lightTheme : darkTheme;
  }, [isAuthenticated, currentPath]);

  return theme;
};

/**
 * Custom hook that tracks the current pathname, working outside of React Router context.
 * Listens to both browser navigation and programmatic navigation changes.
 */
const useCurrentPath = () => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };

    // Listen for browser navigation (back/forward buttons)
    window.addEventListener('popstate', handleLocationChange);

    // Listen for programmatic navigation by intercepting history methods
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      handleLocationChange();
    };

    window.history.replaceState = function (...args) {
      originalReplaceState.apply(window.history, args);
      handleLocationChange();
    };

    // Cleanup: restore original methods and remove listeners
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  return currentPath;
};
